import React, { useState, useEffect, useRef } from 'react';
import { get, post } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import Select from 'react-select';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
    FaFileAlt,
    FaChartLine,
    FaUsers,
    FaStore,
    FaMoneyBillWave,
    FaRetweet,
    FaDownload,
    FaCalendarAlt,
    FaEye,
    FaSpinner,
    FaExclamationTriangle,
    FaCheckCircle,
    FaLightbulb,
    FaArrowUp,
    FaArrowDown,
    FaMinus,
    FaStar,
    FaBullhorn,
    FaEnvelope,
    FaCog,
    FaHeart,
    FaClock,
    FaList,
    FaHistory,
    FaInfoCircle
} from 'react-icons/fa';
import {
    MetricCard,
    CohortAnalysis,
    LeadsFunnel,
    SentimentAnalysis,
    ProductMetrics,
    AcquisitionSources
} from '../../components/reporting/ReportComponents';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    AreaChart,
    Area,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell
} from 'recharts';


// Componente para mostrar recomendaciones de IA
const AIRecommendations = ({ recommendations = [] }) => {
    console.log('💡 [AIRecommendations] Renderizando recomendaciones:', {
        recommendations,
        length: recommendations?.length || 0,
        hasData: !!recommendations && recommendations.length > 0
    });

    if (!recommendations || recommendations.length === 0) {
        console.log('⚠️ [AIRecommendations] No hay recomendaciones disponibles');
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                    <FaLightbulb className="text-yellow-500 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">Recomendaciones de IA</h3>
                </div>
                <p className="text-gray-600">No hay recomendaciones disponibles en este momento.</p>
            </div>
        );
    }

    console.log('✅ [AIRecommendations] Mostrando recomendaciones:', recommendations);

    return (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
                <FaLightbulb className="text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">Recomendaciones de IA</h3>
            </div>
            <div className="space-y-3">
                {recommendations.map((recommendation, index) => (
                    <div key={index} className="flex items-start bg-white p-4 rounded-lg border border-blue-100">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                            {index + 1}
                        </div>
                        <p className="text-gray-700">{recommendation}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Componente para gráficas de evolución (actualizado según documentación)
const EvolutionChart = ({ data, title, dataKey, color = "#3b82f6" }) => {
    console.log('📈 [EvolutionChart] Renderizando evolución:', {
        title,
        dataKey,
        dataLength: data?.length || 0,
        data: data
    });

    if (!data || data.length === 0) {
        console.log('⚠️ [EvolutionChart] No hay datos de evolución');
        return (
            <div className="w-full h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="text-center">
                    <p className="text-gray-500 mb-2">{title}</p>
                    <p className="text-sm text-gray-400">No hay datos disponibles para este período</p>
                    <p className="text-xs text-gray-400 mt-2">El array de evolución está vacío</p>
                </div>
            </div>
        );
    }

    // Normalizar datos: usar totalSales o sales según la documentación
    const normalizedData = data.map(item => ({
        period: item.period || item.month || '',
        // Priorizar totalSales (formato del backend), luego sales (formato legacy), luego 0
        totalSales: item.totalSales || item.sales || 0,
        // Mantener sales para compatibilidad
        sales: item.totalSales || item.sales || 0,
        totalTickets: item.totalTickets || 0
    }));

    // Verificar si hay datos con valores > 0
    const hasDataWithValues = normalizedData.some(item => item.totalSales > 0);
    console.log('📈 [EvolutionChart] Has data with values > 0:', hasDataWithValues);

    if (!hasDataWithValues) {
        console.log('⚠️ [EvolutionChart] Todos los valores son 0');
        return (
            <div className="w-full h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="text-center">
                    <p className="text-gray-500 mb-2">{title}</p>
                    <p className="text-sm text-gray-400">No hay ventas registradas en este período</p>
                    <p className="text-xs text-gray-400 mt-2">Todos los valores están en 0</p>
                </div>
            </div>
        );
    }

    // Formatear período para mostrar: convertir "YYYY-MM" a formato más legible
    const formatPeriod = (period) => {
        if (!period) return '';
        // Si es formato "YYYY-MM", convertir a "MMM YYYY"
        if (/^\d{4}-\d{2}$/.test(period)) {
            const date = new Date(period + '-01');
            return date.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
        }
        // Si ya es un formato legible, retornarlo tal cual
        return period;
    };

    const chartData = normalizedData.map(item => ({
        ...item,
        periodLabel: formatPeriod(item.period)
    }));

    return (
        <div className="w-full h-64">
            <h4 className="text-lg font-semibold mb-4 text-gray-800">{title}</h4>
            <ResponsiveContainer>
                <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="periodLabel"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                    />
                    <YAxis
                        tickFormatter={(value) => `€${value.toFixed(0)}`}
                    />
                    <Tooltip
                        formatter={(value) => [`€${value.toFixed(2)}`, 'Ventas Totales']}
                        labelFormatter={(label) => `Período: ${label}`}
                    />
                    <Legend />
                    <Line
                        type="monotone"
                        dataKey="totalSales"
                        stroke={color}
                        fill={color}
                        name="Ventas Totales"
                        strokeWidth={2}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

// Componente para gráfica de distribución de clientes (actualizado según documentación)
const CustomerDistributionChart = ({ data }) => {
    console.log('📊 [CustomerDistributionChart] Renderizando distribución:', data);

    if (!data || Object.keys(data).length === 0) {
        console.log('⚠️ [CustomerDistributionChart] No hay datos de distribución');
        return (
            <div className="w-full h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <p className="text-gray-500">No hay datos de distribución disponibles</p>
            </div>
        );
    }

    // Mapear nombres de segmentos RFM a nombres más legibles (según documentación)
    const segmentNames = {
        champions: 'Champions',
        loyalCustomers: 'Clientes Fieles',
        potentialLoyalists: 'Potenciales Leales',
        newCustomers: 'Clientes Nuevos',
        promising: 'Prometedores',
        needAttention: 'Necesitan Atención',
        aboutToSleep: 'A Punto de Dormir',
        atRisk: 'En Riesgo',
        cannotLoseThem: 'No Pueden Perderse',
        hibernating: 'Hibernando',
        lost: 'Perdidos'
    };

    // Convertir objeto a array y filtrar valores > 0 (según documentación)
    const chartData = Object.entries(data)
        .filter(([key, value]) => (value || 0) > 0) // Solo mostrar segmentos con clientes
        .map(([key, value]) => ({
            name: segmentNames[key] || key,
            value: value || 0,
            originalKey: key
        }));

    const total = chartData.reduce((sum, item) => sum + item.value, 0);

    // Verificar si hay datos con valores > 0
    const hasDataWithValues = total > 0;
    console.log('📊 [CustomerDistributionChart] Has data with values > 0:', hasDataWithValues, 'Total:', total);

    if (!hasDataWithValues) {
        console.log('⚠️ [CustomerDistributionChart] Todos los valores son 0');
        return (
            <div className="w-full h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="text-center">
                    <p className="text-gray-500 mb-2">Distribución de Clientes (RFM)</p>
                    <p className="text-sm text-gray-400">No hay clientes segmentados en este período</p>
                    <p className="text-xs text-gray-400 mt-2">Todos los segmentos están en 0</p>
                </div>
            </div>
        );
    }

    // Colores según documentación
    const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#C9CBCF', '#FF6384', '#E7E9ED', '#CCCCCC', '#3b82f6'];

    // Función para renderizar etiquetas con porcentajes
    const renderLabel = (entry) => {
        const percentage = ((entry.value / total) * 100).toFixed(1);
        return `${entry.name}: ${percentage}%`;
    };

    return (
        <div className="w-full h-64">
            <h4 className="text-lg font-semibold mb-4 text-gray-800">Distribución de Clientes (RFM)</h4>
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderLabel}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(value, name) => {
                            const percentage = ((value / total) * 100).toFixed(1);
                            return [`${value} (${percentage}%)`, name];
                        }}
                    />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

// Componente principal de la página de reporting
const ReportingPage = () => {
    const [marcas, setMarcas] = useState([]);
    const [selectedMarca, setSelectedMarca] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportType, setReportType] = useState('ejecutivo');
    const [generatedReports, setGeneratedReports] = useState([]);
    const [showReportList, setShowReportList] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [period, setPeriod] = useState('monthly');
    const [advancedParams, setAdvancedParams] = useState({
        tz: 'Europe/Madrid',
        locale: 'es-ES',
        currency: 'EUR',
        granularity: 'month',
        format: 'json',
        compare_to: 'previous_period'
    });
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [jobStatus, setJobStatus] = useState(null);
    const [isGeneratingJob, setIsGeneratingJob] = useState(false);
    const reportRef = useRef(null);

    // Opciones de tipos de reportes - Solo ejecutivo disponible por ahora
    const reportTypes = [
        {
            value: 'ejecutivo',
            label: 'Reporte Ejecutivo',
            icon: <FaChartLine />,
            description: 'Consolidación estratégica mensual/anual con comparativas y tendencias',
            color: 'blue',
            endpoint: 'ejecutivo'
        }
        // Los siguientes reportes están en desarrollo y deshabilitados temporalmente
        // {
        //     value: 'predictivo',
        //     label: 'Análisis Predictivo',
        //     icon: <FaCalendarAlt />,
        //     description: 'Proyecciones futuras y análisis de estacionalidad',
        //     color: 'purple',
        //     endpoint: 'predictivo'
        // },
        // {
        //     value: 'canales',
        //     label: 'Análisis de Canales',
        //     icon: <FaBullhorn />,
        //     description: 'ROI por canal y optimización de marketing',
        //     color: 'green',
        //     endpoint: 'canales'
        // },
        // {
        //     value: 'cohortes',
        //     label: 'Análisis de Cohortes',
        //     icon: <FaUsers />,
        //     description: 'Retención a largo plazo y LTV por cohorte',
        //     color: 'indigo',
        //     endpoint: 'cohortes'
        // },
        // {
        //     value: 'productos',
        //     label: 'Análisis de Productos',
        //     icon: <FaStore />,
        //     description: 'Productos rentables, correlaciones y cross-sell',
        //     color: 'yellow',
        //     endpoint: 'productos'
        // },
        // {
        //     value: 'campanas-roi',
        //     label: 'ROI de Campañas',
        //     icon: <FaMoneyBillWave />,
        //     description: 'ROI detallado con atribución y comparativas',
        //     color: 'orange',
        //     endpoint: 'campanas-roi'
        // },
        // {
        //     value: 'benchmarking',
        //     label: 'Benchmarking',
        //     icon: <FaStar />,
        //     description: 'Comparación con estándares del sector',
        //     color: 'pink',
        //     endpoint: 'benchmarking'
        // },
        // {
        //     value: 'sentimiento',
        //     label: 'Análisis de Sentimiento',
        //     icon: <FaHeart />,
        //     description: 'Reputación y satisfacción con análisis de temas',
        //     color: 'red',
        //     endpoint: 'sentimiento'
        // }
    ];

    // Opciones de períodos
    const periodOptions = [
        { value: 'weekly', label: 'Semanal' },
        { value: 'monthly', label: 'Mensual' },
        { value: 'quarterly', label: 'Trimestral' },
        { value: 'yearly', label: 'Anual' }
    ];

    useEffect(() => {
        const fetchMarcas = async () => {
            console.log('🏢 [ReportingPage] Cargando marcas...');
            try {
                const response = await get('marcas');
                console.log('✅ [ReportingPage] Marcas cargadas exitosamente:', {
                    cantidad: response?.length || 0,
                    marcas: response?.map(m => ({ id: m._id, nombre: m.nombre })) || [],
                    timestamp: new Date().toISOString()
                });
                setMarcas(response);
                if (response.length > 0) {
                    console.log('🎯 [ReportingPage] Marca seleccionada por defecto:', {
                        marcaId: response[0]._id,
                        marcaNombre: response[0].nombre
                    });
                    setSelectedMarca(response[0]);
                } else {
                    console.warn('⚠️ [ReportingPage] No se encontraron marcas');
                }
            } catch (error) {
                console.error('❌ [ReportingPage] Error cargando marcas:', {
                    error: error.message,
                    stack: error.stack,
                    timestamp: new Date().toISOString()
                });
                Swal.fire('Error', 'No se pudieron cargar las marcas.', 'error');
            }
        };
        fetchMarcas();
    }, []);

    // Cargar reportes generados al montar el componente
    useEffect(() => {
        if (selectedMarca) {
            loadGeneratedReports();
        }
    }, [selectedMarca]);

    // No generar reporte automáticamente - ahora es manual
    // useEffect(() => {
    //     if (selectedMarca) {
    //         generateReport();
    //     }
    // }, [selectedMarca, reportType, period]);

    // Función para cargar reportes generados
    const loadGeneratedReports = async () => {
        if (!selectedMarca) return;

        try {
            const response = await get(`reporting/reports?marcaId=${selectedMarca._id}&page=1&limit=20`);

            if (response && response.reports) {
                console.log('✅ [REPORTS] Reportes cargados:', response.reports.length);
                setGeneratedReports(response.reports || []);
            }
        } catch (error) {
            console.error('❌ [REPORTS] Error cargando reportes:', error);
            // No mostrar error al usuario, simplemente dejar lista vacía
        }
    };

    // Función simplificada para generar reporte usando el endpoint de reporting o fallback a estadísticas
    const generateReport = async () => {
        if (!selectedMarca) return;

        console.log(`📊 [REPORT] Generando reporte ejecutivo para ${selectedMarca.nombre} (${period})`);

        setLoading(true);
        setError(null);
        setIsGeneratingJob(false);
        setJobStatus(null);

        try {
            // Primero intentar usar el endpoint de reporting del backend según la documentación
            // GET /api/reporting/general?marcaId=...&period=...
            let reportData = null;

            try {
                console.log('🔍 [REPORT] Intentando endpoint de reporting:', `reporting/general?marcaId=${selectedMarca._id}&period=${period}`);
                const reportingResponse = await get(`reporting/general?marcaId=${selectedMarca._id}&period=${period}`);

                if (reportingResponse && reportingResponse.reportId) {
                    console.log('✅ [REPORT] Respuesta del endpoint de reporting:', {
                        reportId: reportingResponse.reportId,
                        hasExecutiveSummary: !!reportingResponse.executiveSummary,
                        hasCharts: !!reportingResponse.charts
                    });

                    // Convertir respuesta del backend al formato esperado
                    reportData = convertBackendReportToFrontendFormat(reportingResponse);
                }
            } catch (reportingError) {
                console.warn('⚠️ [REPORT] Endpoint de reporting no disponible, usando endpoints de estadísticas:', reportingError.message);
                // Si falla, continuar con el cálculo desde estadísticas
            }

            // Si el endpoint de reporting no funcionó, calcular desde estadísticas
            if (!reportData) {
                console.log('📊 [REPORT] Calculando desde endpoints de estadísticas');

                // Calcular fechas del período
                const now = new Date();
                let startDate, endDate, daysBack;

                switch (period) {
                    case 'weekly':
                        daysBack = 7;
                        break;
                    case 'monthly':
                        daysBack = 30;
                        break;
                    case 'quarterly':
                        daysBack = 90;
                        break;
                    case 'yearly':
                        daysBack = 365;
                        break;
                    default:
                        daysBack = 30;
                }

                startDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
                endDate = now;

                console.log('📅 [REPORT] Período calculado:', {
                    period,
                    daysBack,
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                });

                // Usar los mismos endpoints que funcionan en las estadísticas
                const [salesMonthly, newCustomersMonthly, avgTicketMonthly, ticketsData, clientesData, statsDetailed] = await Promise.allSettled([
                    get(`statistics/sales/monthly/${selectedMarca._id}`).catch(() => []),
                    get(`statistics/new-customers/monthly/${selectedMarca._id}`).catch(() => []),
                    get(`statistics/average-ticket/monthly/${selectedMarca._id}`).catch(() => []),
                    get(`tickets/marca/${selectedMarca._id}`).catch(() => []),
                    get(`clientes?marca=${selectedMarca._id}`).catch(() => []),
                    get(`statistics/marca/${selectedMarca._id}/detailed`).catch(() => null)
                ]);

                const sales = salesMonthly.status === 'fulfilled' ? salesMonthly.value : [];
                const newCustomers = newCustomersMonthly.status === 'fulfilled' ? newCustomersMonthly.value : [];
                const avgTicket = avgTicketMonthly.status === 'fulfilled' ? avgTicketMonthly.value : [];
                const tickets = ticketsData.status === 'fulfilled' ? ticketsData.value : [];
                const clientes = clientesData.status === 'fulfilled' ? clientesData.value : [];
                const stats = statsDetailed.status === 'fulfilled' ? statsDetailed.value : null;

                console.log('📊 [REPORT] Datos obtenidos:', {
                    sales: sales.length,
                    newCustomers: newCustomers.length,
                    avgTicket: avgTicket.length,
                    tickets: tickets.length,
                    clientes: clientes.length,
                    hasStats: !!stats
                });

                // Filtrar tickets del período
                const ticketsInPeriod = tickets.filter(ticket => {
                    const ticketDate = new Date(ticket.created_at || ticket.createdAt || ticket.fecha);
                    return ticketDate >= startDate && ticketDate <= endDate;
                });

                // Calcular métricas desde tickets reales (igual que las estadísticas)
                let totalVentas = 0;
                let totalTransacciones = 0; // Contar transacciones reales (historialConsumiciones)
                let totalPuntosAcumulados = 0;
                let totalPuntosCanjeados = 0;
                const clientesUnicos = new Set();
                const ventasPorDia = new Map();

                ticketsInPeriod.forEach(ticket => {
                    // Ventas desde historialConsumiciones (igual que StoreStatisticsPage)
                    if (ticket.historialConsumiciones && ticket.historialConsumiciones.length > 0) {
                        ticket.historialConsumiciones.forEach(consumicion => {
                            const monto = consumicion.monto || consumicion.total || 0;
                            if (monto > 0) {
                                totalVentas += monto;
                                totalTransacciones += 1;
                                totalPuntosAcumulados += consumicion.puntos || 0;
                                clientesUnicos.add(ticket.cliente?.toString());

                                // Agrupar por día para evolución
                                const fecha = new Date(consumicion.fecha || ticket.created_at || ticket.createdAt);
                                const diaKey = fecha.toISOString().split('T')[0];
                                ventasPorDia.set(diaKey, (ventasPorDia.get(diaKey) || 0) + monto);
                            }
                        });
                    } else if (ticket.total && ticket.total > 0) {
                        // Fallback si no hay historialConsumiciones pero hay total
                        totalVentas += ticket.total;
                        totalTransacciones += 1;
                        totalPuntosAcumulados += ticket.puntos || 0;
                        clientesUnicos.add(ticket.cliente?.toString());

                        const fecha = new Date(ticket.created_at || ticket.createdAt);
                        const diaKey = fecha.toISOString().split('T')[0];
                        ventasPorDia.set(diaKey, (ventasPorDia.get(diaKey) || 0) + ticket.total);
                    }

                    // Puntos canjeados
                    if (ticket.historialCanjeos) {
                        ticket.historialCanjeos.forEach(canje => {
                            totalPuntosCanjeados += canje.puntos || 0;
                        });
                    }
                });

                const totalClientes = clientesUnicos.size;
                const ticketPromedio = totalTransacciones > 0 ? totalVentas / totalTransacciones : 0;

                // Calcular nuevos clientes en el período
                const clientesEnPeriodo = clientes.filter(cliente => {
                    const fechaCreacion = new Date(cliente.fechaCreacion || cliente.createdAt || cliente.created_at);
                    return fechaCreacion >= startDate && fechaCreacion <= endDate;
                });

                // Calcular evolución de ventas mensual desde salesMonthly (formato según documentación)
                const salesEvolution = sales.map(item => {
                    // Convertir formato de mes a "YYYY-MM" si es necesario
                    const period = item.month || item.period || '';
                    let periodFormatted = period;

                    // Si el período viene en formato "YYYY-MM", mantenerlo
                    // Si viene en otro formato, intentar convertirlo
                    if (period && !/^\d{4}-\d{2}$/.test(period)) {
                        // Intentar parsear y convertir a formato "YYYY-MM"
                        const date = new Date(period);
                        if (!isNaN(date.getTime())) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            periodFormatted = `${year}-${month}`;
                        }
                    }

                    return {
                        period: periodFormatted || period,
                        totalSales: item.sales || 0,
                        sales: item.sales || 0, // Mantener para compatibilidad
                        totalTickets: item.totalTickets || 0
                    };
                }).filter(item => item.totalSales > 0);

                // Si no hay salesMonthly, usar ventasPorDia agrupando por mes
                if (salesEvolution.length === 0 && ventasPorDia.size > 0) {
                    const sortedDays = Array.from(ventasPorDia.entries())
                        .sort(([a], [b]) => a.localeCompare(b))
                        .slice(-daysBack <= 30 ? 30 : daysBack <= 90 ? 90 : 365);

                    // Agrupar por mes (formato "YYYY-MM")
                    const ventasPorMes = new Map();
                    sortedDays.forEach(([date, ventas]) => {
                        const fecha = new Date(date);
                        const year = fecha.getFullYear();
                        const month = String(fecha.getMonth() + 1).padStart(2, '0');
                        const monthKey = `${year}-${month}`;
                        ventasPorMes.set(monthKey, (ventasPorMes.get(monthKey) || 0) + ventas);
                    });

                    // Convertir a array con formato según documentación
                    salesEvolution.push(...Array.from(ventasPorMes.entries()).map(([period, totalSales]) => ({
                        period: period, // Formato "YYYY-MM"
                        totalSales: totalSales,
                        sales: totalSales, // Mantener para compatibilidad
                        totalTickets: 0
                    })));
                }

                // Calcular clientes recurrentes (que tienen más de 1 transacción)
                const transaccionesPorCliente = new Map();
                ticketsInPeriod.forEach(ticket => {
                    const clienteId = ticket.cliente?.toString();
                    if (clienteId) {
                        // Contar transacciones reales (historialConsumiciones)
                        if (ticket.historialConsumiciones && ticket.historialConsumiciones.length > 0) {
                            const transacciones = ticket.historialConsumiciones.filter(c => (c.monto || c.total || 0) > 0).length;
                            if (transacciones > 0) {
                                transaccionesPorCliente.set(clienteId, (transaccionesPorCliente.get(clienteId) || 0) + transacciones);
                            }
                        } else if (ticket.total && ticket.total > 0) {
                            // Fallback si no hay historialConsumiciones
                            transaccionesPorCliente.set(clienteId, (transaccionesPorCliente.get(clienteId) || 0) + 1);
                        }
                    }
                });
                const clientesRecurrentes = Array.from(transaccionesPorCliente.values()).filter(count => count > 1).length;

                // Obtener satisfacción desde stats si está disponible
                const averageSatisfaction = stats?.averageSatisfaction || 0;
                const npsScore = stats?.npsScore || 50;

                // Calcular distribución RFM básica (simplificada)
                const customerDistribution = {
                    champions: 0,
                    loyalCustomers: 0,
                    potentialLoyalists: 0,
                    newCustomers: clientesEnPeriodo.length,
                    promising: 0,
                    needAttention: 0,
                    aboutToSleep: 0,
                    atRisk: 0,
                    cannotLoseThem: 0,
                    hibernating: 0,
                    lost: 0
                };

                // Generar recomendaciones simples basadas en datos reales
                const recomendaciones = [];
                if (ticketPromedio < 20) {
                    recomendaciones.push('💡 Tu ticket medio es de €' + ticketPromedio.toFixed(2) + '. Implementa estrategias de upselling: combos, productos complementarios o descuentos por compra mínima.');
                }
                if (clientesRecurrentes === 0 && totalClientes > 0) {
                    recomendaciones.push('🔄 Tasa de retorno baja. Implementa un programa de fidelización más agresivo: puntos por compra, descuentos por frecuencia, o membresías.');
                }
                if (totalPuntosAcumulados > 0 && totalPuntosCanjeados / totalPuntosAcumulados < 0.3) {
                    recomendaciones.push('🎁 Promociona tus recompensas disponibles para aumentar la tasa de canje de puntos.');
                }
                if (salesEvolution.length === 0) {
                    recomendaciones.push('📈 No hay datos de ventas en este período. Considera campañas promocionales para generar actividad.');
                }

                // Construir reporte simplificado desde estadísticas
                reportData = {
                    reportId: `RPT-${Date.now()}-${selectedMarca._id}`,
                    generatedAt: new Date().toISOString(),
                    period: period,
                    marcaId: selectedMarca._id,
                    marcaName: selectedMarca.nombre,
                    dataSource: 'statistics_endpoints',
                    title: `Reporte Ejecutivo ${selectedMarca.nombre}`,
                    description: `Reporte consolidado para el período ${period}`,
                    periodStart: startDate.toISOString(),
                    periodEnd: endDate.toISOString(),
                    executiveSummary: {
                        ventasActuales: totalVentas,
                        ventasAnteriores: 0, // Se puede calcular si hay datos históricos
                        variacionVentas: 0,
                        ticketMedioActual: ticketPromedio,
                        ticketMedioAnterior: 0,
                        variacionTicket: 0,
                        nuevosClientes: clientesEnPeriodo.length,
                        clientesRecurrentes: clientesRecurrentes,
                        clientesPerdidos: 0,
                        npsScore: npsScore,
                        averageSatisfaction: averageSatisfaction,
                        topRecommendations: recomendaciones
                    },
                    charts: {
                        salesEvolution: salesEvolution,
                        customerDistribution: customerDistribution,
                        returnRate: totalClientes > 0 ? (clientesRecurrentes / totalClientes) * 100 : 0,
                        satisfaction: {
                            npsScore: npsScore,
                            averageScore: averageSatisfaction,
                            totalResponses: 0
                        }
                    },
                    campaigns: {
                        campaignMetrics: {
                            totalCampaigns: 0,
                            activeCampaigns: 0,
                            openRate: 0,
                            clickRate: 0,
                            redemptionRate: 0,
                            estimatedROI: 0
                        },
                        campaignDetails: []
                    },
                    loyalty: {
                        loyaltyMetrics: {
                            pointsAccumulated: totalPuntosAcumulados,
                            pointsRedeemed: totalPuntosCanjeados,
                            redemptionRate: totalPuntosAcumulados > 0 ? (totalPuntosCanjeados / totalPuntosAcumulados) * 100 : 0,
                            averagePointsPerCustomer: totalClientes > 0 ? totalPuntosAcumulados / totalClientes : 0
                        },
                        topLoyalCustomers: [],
                        popularRewards: []
                    },
                    recomendacionesAccionables: recomendaciones
                };
            }

            console.log('✅ [REPORT] Reporte generado:', {
                ventas: reportData?.executiveSummary?.ventasActuales || reportData?.executiveSummary?.totalSales || 0,
                clientes: reportData?.executiveSummary?.nuevosClientes || reportData?.executiveSummary?.newCustomers || 0,
                ticket: reportData?.executiveSummary?.ticketMedioActual || reportData?.executiveSummary?.averageTicket || 0,
                salesEvolutionLength: reportData?.charts?.salesEvolution?.length || 0,
                dataSource: reportData?.dataSource || 'unknown'
            });

            setReportData(reportData);
            setLoading(false);

            Swal.fire('Éxito', 'Reporte generado exitosamente.', 'success');
        } catch (error) {
            console.error('❌ [ERROR] Error generando reporte:', {
                marca: selectedMarca.nombre,
                error: error.message
            });
            setError('Error al generar el reporte. Por favor, inténtalo de nuevo.');
            Swal.fire('Error', 'No se pudo generar el reporte.', 'error');
            setLoading(false);
        }
    };

    // Función para convertir respuesta del endpoint de reporting al formato frontend
    const convertBackendReportToFrontendFormat = (backendResponse) => {
        console.log('🔄 [CONVERT] Convirtiendo respuesta del backend de reporting:', {
            reportId: backendResponse.reportId,
            hasExecutiveSummary: !!backendResponse.executiveSummary,
            hasCharts: !!backendResponse.charts
        });

        // Mapear campos del backend al formato esperado por el frontend
        const converted = {
            reportId: backendResponse.reportId || `RPT-${Date.now()}-${selectedMarca?._id}`,
            generatedAt: backendResponse.generatedAt || new Date().toISOString(),
            period: backendResponse.period || period,
            marcaId: backendResponse.marcaId || selectedMarca?._id,
            marcaName: backendResponse.marcaName || selectedMarca?.nombre,
            dataSource: 'reporting_endpoint',
            title: backendResponse.title || `Reporte Ejecutivo ${selectedMarca?.nombre}`,
            description: backendResponse.description || '',
            periodStart: backendResponse.periodStart || backendResponse.startDate,
            periodEnd: backendResponse.periodEnd || backendResponse.endDate,
            executiveSummary: {
                // Mapear campos del backend al formato esperado
                ventasActuales: backendResponse.executiveSummary?.totalSales || backendResponse.executiveSummary?.ventasActuales || 0,
                ventasAnteriores: backendResponse.executiveSummary?.salesVariation ? (backendResponse.executiveSummary.totalSales - (backendResponse.executiveSummary.totalSales * backendResponse.executiveSummary.salesVariation / 100)) : 0,
                variacionVentas: backendResponse.executiveSummary?.salesVariation || backendResponse.executiveSummary?.variacionVentas || 0,
                ticketMedioActual: backendResponse.executiveSummary?.averageTicket || backendResponse.executiveSummary?.ticketMedioActual || 0,
                ticketMedioAnterior: backendResponse.executiveSummary?.averageTicket ? (backendResponse.executiveSummary.averageTicket / (1 + (backendResponse.executiveSummary.salesVariation || 0) / 100)) : 0,
                variacionTicket: backendResponse.executiveSummary?.ticketVariation || 0,
                nuevosClientes: backendResponse.executiveSummary?.newCustomers || backendResponse.executiveSummary?.nuevosClientes || 0,
                clientesRecurrentes: backendResponse.executiveSummary?.returningCustomers || backendResponse.executiveSummary?.clientesRecurrentes || 0,
                clientesPerdidos: backendResponse.executiveSummary?.lostCustomers || backendResponse.executiveSummary?.clientesPerdidos || 0,
                npsScore: backendResponse.executiveSummary?.npsScore || 50,
                averageSatisfaction: backendResponse.executiveSummary?.averageSatisfaction || 0,
                topRecommendations: backendResponse.executiveSummary?.topRecommendations || backendResponse.aiRecommendations || []
            },
            charts: {
                // Normalizar salesEvolution al formato esperado (totalSales, period en "YYYY-MM")
                salesEvolution: (backendResponse.charts?.salesEvolution || backendResponse.charts?.salesHistory || []).map(item => ({
                    period: item.period || item.month || '',
                    totalSales: item.totalSales || item.sales || 0,
                    sales: item.totalSales || item.sales || 0, // Mantener para compatibilidad
                    totalTickets: item.totalTickets || 0
                })),
                customerDistribution: backendResponse.charts?.customerDistribution || {},
                returnRate: backendResponse.charts?.returnRate || 0,
                satisfaction: backendResponse.charts?.satisfaction || {
                    npsScore: backendResponse.executiveSummary?.npsScore || 50,
                    averageScore: backendResponse.executiveSummary?.averageSatisfaction || 0,
                    totalResponses: 0
                }
            },
            campaigns: backendResponse.campaigns || {
                campaignMetrics: {
                    totalCampaigns: 0,
                    activeCampaigns: 0,
                    openRate: 0,
                    clickRate: 0,
                    redemptionRate: 0,
                    estimatedROI: 0
                },
                campaignDetails: []
            },
            loyalty: backendResponse.loyalty || {
                loyaltyMetrics: {
                    pointsAccumulated: 0,
                    pointsRedeemed: 0,
                    redemptionRate: 0,
                    averagePointsPerCustomer: 0
                },
                topLoyalCustomers: [],
                popularRewards: []
            },
            recomendacionesAccionables: backendResponse.executiveSummary?.topRecommendations || backendResponse.aiRecommendations || []
        };

        console.log('✅ [CONVERT] Conversión completada:', {
            reportId: converted.reportId,
            ventas: converted.executiveSummary.ventasActuales,
            clientes: converted.executiveSummary.nuevosClientes,
            ticket: converted.executiveSummary.ticketMedioActual
        });

        return converted;
    };

    // Función para cargar datos de un reporte específico desde la lista (si se usa en el futuro)
    const loadReportData = async (reportId) => {
        // Esta función se mantiene para compatibilidad con la lista de reportes generados
        // Pero ya no se usa para generar nuevos reportes
        try {
            setLoading(true);
            const response = await get(`reporting/reports/${reportId}`);
            if (response && response.data) {
                const convertedData = convertReportDataToFrontendFormat(response);
                setReportData(convertedData);
                setSelectedReport(response);
            }
        } catch (error) {
            console.error('❌ [REPORT] Error cargando reporte guardado:', error);
            Swal.fire('Error', 'No se pudieron cargar los datos del reporte guardado.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Función para convertir datos del reporte al formato frontend
    const convertReportDataToFrontendFormat = (reportResponse) => {
        const data = reportResponse.data || {};
        console.log('🔄 [CONVERT] Convirtiendo datos del reporte:', {
            reportType,
            hasResumenEjecutivo: !!data.resumenEjecutivo,
            hasCharts: !!data.charts,
            hasCampaigns: !!data.campaigns,
            hasLoyalty: !!data.loyalty
        });

        // Estructura base
        const converted = {
            reportId: reportResponse.reportId,
            generatedAt: reportResponse.createdAt || new Date().toISOString(),
            period: reportResponse.periodo || period,
            marcaId: reportResponse.marcaId,
            marcaName: reportResponse.marcaName || selectedMarca?.nombre,
            dataSource: 'backend_v2',
            title: reportResponse.title || `${reportTypes.find(rt => rt.value === reportType)?.label || reportType}`,
            description: reportResponse.description || '',
            periodStart: reportResponse.periodStart,
            periodEnd: reportResponse.periodEnd
        };

        // Mapear datos según el tipo de reporte
        switch (reportType) {
            case 'ejecutivo':
                // Mapear resumen ejecutivo
                converted.executiveSummary = data.resumenEjecutivo || {};

                // Mapear charts
                converted.charts = data.charts || {};

                // Mapear campañas
                converted.campaigns = data.campaigns || {};

                // Mapear fidelización
                converted.loyalty = data.loyalty || {};

                // Datos adicionales
                converted.comparativaAnual = data.comparativaAnual || {};
                converted.tendencias = data.tendencias || {};
                converted.benchmarking = data.benchmarking || {};
                converted.insights = data.insights || [];
                converted.recomendacionesAccionables = data.recomendacionesAccionables || [];

                // Mapear recomendaciones a topRecommendations si no están en executiveSummary
                if (!converted.executiveSummary.topRecommendations && converted.recomendacionesAccionables && converted.recomendacionesAccionables.length > 0) {
                    converted.executiveSummary.topRecommendations = converted.recomendacionesAccionables;
                    console.log('✅ [CONVERT] Recomendaciones mapeadas:', converted.recomendacionesAccionables.length, 'elementos');
                }

                // Si no hay resumen ejecutivo pero hay salesHistory o customerAnalysis, construir uno
                if (!converted.executiveSummary || Object.keys(converted.executiveSummary).length === 0) {
                    console.log('⚠️ [CONVERT] No hay resumenEjecutivo, intentando construir desde otras secciones');

                    const salesHistory = data.salesHistory || {};
                    const customerAnalysis = data.customerAnalysis || {};
                    const satisfaction = data.satisfaction || {};

                    converted.executiveSummary = {
                        totalSales: salesHistory.salesHistory?.reduce((sum, day) => sum + (day.totalSales || 0), 0) || 0,
                        salesVariation: salesHistory.variations?.salesVariation || 0,
                        averageTicket: salesHistory.salesHistory?.length > 0
                            ? salesHistory.salesHistory.reduce((sum, day) => sum + (day.averageTicket || 0), 0) / salesHistory.salesHistory.length
                            : 0,
                        newCustomers: customerAnalysis.customerSegmentation?.new || 0,
                        returningCustomers: customerAnalysis.customerSegmentation?.active || 0,
                        lostCustomers: 0,
                        averageSatisfaction: satisfaction.averageSatisfaction || 0,
                        npsScore: satisfaction.npsScore || 0,
                        topRecommendations: data.recomendacionesAccionables || []
                    };

                    console.log('✅ [CONVERT] executiveSummary construido:', converted.executiveSummary);
                }

                // Si no hay charts pero hay salesHistory, construir charts
                if (!converted.charts || Object.keys(converted.charts).length === 0) {
                    console.log('⚠️ [CONVERT] No hay charts, intentando construir desde salesHistory');

                    const salesHistory = data.salesHistory || {};
                    const customerAnalysis = data.customerAnalysis || {};

                    converted.charts = {
                        salesEvolution: salesHistory.salesHistory?.map(day => ({
                            period: new Date(day.date || day.period || Date.now()).toLocaleDateString('es-ES', { month: 'short' }),
                            sales: day.totalSales || 0
                        })) || [],
                        customerDistribution: customerAnalysis.customerSegmentation || {},
                        returnRate: customerAnalysis.customerBehavior?.retentionRate || 0,
                        satisfaction: {
                            npsScore: data.satisfaction?.npsScore || 0,
                            averageScore: data.satisfaction?.averageSatisfaction || 0,
                            totalResponses: data.satisfaction?.totalResponses || 0
                        }
                    };

                    console.log('✅ [CONVERT] charts construido:', converted.charts);
                }

                break;
            // Los siguientes tipos de reportes están en desarrollo
            // case 'predictivo':
            //     converted.proyecciones = data.proyecciones || {};
            //     converted.estacionalidad = data.estacionalidad || {};
            //     converted.riesgos = data.riesgos || [];
            //     break;
            // case 'canales':
            //     converted.canales = data.canales || [];
            //     converted.reasignacionPresupuesto = data.reasignacionPresupuesto || {};
            //     break;
            // case 'cohortes':
            //     converted.cohortesRetencion = data.cohortesRetencion || {};
            //     converted.cohortesAltoValor = data.cohortesAltoValor || [];
            //     converted.comparativaCohortes = data.comparativaCohortes || {};
            //     break;
            // case 'productos':
            //     converted.productosRentables = data.productosRentables || [];
            //     converted.correlaciones = data.correlaciones || [];
            //     converted.oportunidadesCrossSell = data.oportunidadesCrossSell || [];
            //     converted.productosEnDeclive = data.productosEnDeclive || [];
            //     break;
            // case 'campanas-roi':
            //     converted.campanas = data.campanas || [];
            //     converted.atribucion = data.atribucion || {};
            //     converted.campanasSimilares = data.campanasSimilares || {};
            //     break;
            // case 'benchmarking':
            //     converted.benchmarkingSector = data.benchmarkingSector || {};
            //     converted.rankingInterno = data.rankingInterno || {};
            //     converted.objetivosSMART = data.objetivosSMART || [];
            //     break;
            // case 'sentimiento':
            //     converted.sentimientoGeneral = data.sentimientoGeneral || {};
            //     converted.temasMasMencionados = data.temasMasMencionados || [];
            //     converted.evolucion = data.evolucion || {};
            //     converted.planAccion = data.planAccion || [];
            //     break;
        }

        // Agregar datos raw para referencia
        converted.rawData = data;

        console.log('✅ [CONVERT] Conversión completada. Datos finales:', {
            reportId: converted.reportId,
            reportType: reportType,
            hasExecutiveSummary: !!converted.executiveSummary,
            executiveSummaryKeys: converted.executiveSummary ? Object.keys(converted.executiveSummary) : [],
            hasCharts: !!converted.charts,
            chartsKeys: converted.charts ? Object.keys(converted.charts) : [],
            hasCampaigns: !!converted.campaigns,
            hasLoyalty: !!converted.loyalty,
            fullConvertedObject: converted
        });

        return converted;
    };

    // Función para convertir nueva estructura de API v2.0 a formato frontend
    const convertNewApiResponseToFrontendFormat = (apiResponse) => {
        console.log('🔄 [CONVERT] Convirtiendo respuesta API v2.0 a formato frontend');

        // Extraer datos de las nuevas secciones específicas
        const salesHistory = apiResponse.salesHistory || {};
        const customerAnalysis = apiResponse.customerAnalysis || {};
        const satisfaction = apiResponse.satisfaction || {};
        const campaigns = apiResponse.campaigns || {};
        const loyalty = apiResponse.loyalty || {};

        // Convertir a formato compatible con el frontend existente
        const convertedData = {
            reportId: apiResponse.reportId,
            generatedAt: apiResponse.generatedAt,
            period: apiResponse.period,
            marcaId: apiResponse.marcaId,
            marcaName: apiResponse.marcaName,

            // Usar executiveSummary si está disponible, sino construir desde las secciones específicas
            executiveSummary: apiResponse.executiveSummary || {
                totalSales: salesHistory.salesHistory?.reduce((sum, day) => sum + (day.totalSales || 0), 0) || 0,
                salesVariation: salesHistory.variations?.salesVariation || 0,
                averageTicket: salesHistory.salesHistory?.length > 0
                    ? salesHistory.salesHistory.reduce((sum, day) => sum + (day.averageTicket || 0), 0) / salesHistory.salesHistory.length
                    : 0,
                newCustomers: customerAnalysis.customerSegmentation?.new || 0,
                returningCustomers: customerAnalysis.customerSegmentation?.active || 0,
                lostCustomers: customerAnalysis.customerBehavior?.churnRate ?
                    Math.round((customerAnalysis.customerSegmentation?.active || 0) * (customerAnalysis.customerBehavior.churnRate / 100)) : 0,
                averageSatisfaction: satisfaction.averageSatisfaction || 0,
                npsScore: satisfaction.npsScore || 0,
                topRecommendations: apiResponse.aiRecommendations || []
            },

            // Construir charts desde las secciones específicas
            charts: {
                salesEvolution: salesHistory.salesHistory?.map(day => ({
                    period: new Date(day.date).toLocaleDateString('es-ES', { month: 'short' }),
                    sales: day.totalSales || 0
                })) || [],
                customerDistribution: customerAnalysis.customerSegmentation || {
                    champions: 0,
                    loyalCustomers: 0,
                    potentialLoyalists: 0,
                    newCustomers: 0,
                    promising: 0,
                    needAttention: 0,
                    aboutToSleep: 0,
                    atRisk: 0,
                    cannotLoseThem: 0,
                    hibernating: 0,
                    lost: 0
                },
                returnRate: customerAnalysis.customerBehavior?.retentionRate || 0,
                satisfaction: {
                    npsScore: satisfaction.npsScore || 0,
                    averageScore: satisfaction.averageSatisfaction || 0,
                    totalResponses: satisfaction.totalResponses || 0
                }
            },

            // Usar datos de campañas de la nueva estructura
            campaigns: campaigns.campaignMetrics || {
                totalCampaigns: 0,
                activeCampaigns: 0,
                openRate: 0,
                clickRate: 0,
                redemptionRate: 0,
                estimatedROI: 0
            },

            // Usar datos de fidelización de la nueva estructura
            loyalty: loyalty.loyaltyMetrics || {
                pointsAccumulated: 0,
                pointsRedeemed: 0,
                topLoyalCustomers: [],
                popularRewards: []
            },

            // Agregar las nuevas secciones específicas para uso futuro
            salesHistory: salesHistory,
            customerAnalysis: customerAnalysis,
            satisfaction: satisfaction,
            loyalty: loyalty,

            // Indicar que los datos vienen del backend v2.0
            dataSource: 'backend_v2',
            missingData: {
                historicalSales: false,
                customerAnalysis: false,
                satisfactionSurveys: false,
                campaignMetrics: false,
                detailedLoyalty: false
            }
        };

        console.log('✅ [APIv2] Conversión completada:', {
            reportId: convertedData.reportId,
            dataSource: convertedData.dataSource,
            hasNewSections: {
                salesHistory: !!convertedData.salesHistory,
                customerAnalysis: !!convertedData.customerAnalysis,
                satisfaction: !!convertedData.satisfaction,
                campaigns: !!convertedData.campaigns,
                loyalty: !!convertedData.loyalty
            }
        });

        return convertedData;
    };

    // Función para generar reporte con datos disponibles
    const generateReportFromAvailableData = async (marca, type, period) => {
        console.log(`📊 [LOCAL] Generando ${type} con datos locales para ${marca.nombre}`);

        // Obtener datos adicionales necesarios
        const [clientesData, ticketsData, premiosData] = await Promise.allSettled([
            get(`marcas/${marca._id}/clientes`).catch(() => []),
            get(`tickets/marca/${marca._id}`).catch(() => []),
            get(`premios/marca/${marca._id}`).catch(() => [])
        ]);

        const clientes = clientesData.status === 'fulfilled' ? clientesData.value : [];
        const tickets = ticketsData.status === 'fulfilled' ? ticketsData.value : [];
        const premios = premiosData.status === 'fulfilled' ? premiosData.value : [];

        console.log('📈 [LOCAL] Datos disponibles:', {
            clientes: clientes.length,
            tickets: tickets.length,
            premios: premios.length,
            marcaClientes: marca.clientes?.length || 0
        });

        // Calcular métricas basadas en datos reales
        const totalClientes = marca.clientes?.length || clientes.length || 0;
        const totalTiendas = marca.tiendas?.length || 0;
        const totalPremios = marca.premios?.length || premios.length || 0;

        // Calcular ventas totales basadas en tickets reales
        const ventasTotales = tickets.reduce((sum, ticket) => sum + (ticket.total || 0), 0);
        const ticketPromedio = tickets.length > 0 ? ventasTotales / tickets.length : 0;

        // Calcular puntos acumulados basados en reglas de la marca
        const puntosAcumulados = tickets.reduce((sum, ticket) => {
            const puntos = Math.floor(ticket.total || 0) * (marca.pointsRules?.ratePerAmount?.rate || 1);
            return sum + puntos;
        }, 0);

        // Calcular puntos canjeados (simulado basado en premios)
        const puntosCanjeados = Math.floor(puntosAcumulados * 0.6); // 60% de canje estimado

        // Generar recomendaciones basadas en datos reales
        const recomendaciones = [];
        if (totalClientes < 50) {
            recomendaciones.push("Implementar campaña de captación de nuevos clientes");
        }
        if (ticketPromedio < 50) {
            recomendaciones.push("Crear ofertas para aumentar el ticket promedio");
        }
        if (puntosCanjeados < puntosAcumulados * 0.3) {
            recomendaciones.push("Promocionar las recompensas disponibles para aumentar canjes");
        }
        if (totalTiendas > 1) {
            recomendaciones.push("Analizar rendimiento por tienda para optimizar recursos");
        }

        const baseData = {
            reportId: `RPT-${Date.now()}-${marca._id}`,
            generatedAt: new Date().toISOString(),
            period: period,
            marcaId: marca._id,
            marcaName: marca.nombre,
            executiveSummary: {
                totalSales: ventasTotales,
                salesVariation: 0, // Requiere datos históricos del backend
                averageTicket: Math.round(ticketPromedio * 100) / 100,
                newCustomers: Math.floor(totalClientes * 0.3), // Estimación
                returningCustomers: Math.floor(totalClientes * 0.7), // Estimación
                lostCustomers: 0, // Requiere análisis de backend
                averageSatisfaction: 8.0, // Requiere datos de encuestas del backend
                topRecommendations: recomendaciones.length > 0 ? recomendaciones : [
                    "Configurar encuestas de satisfacción para obtener NPS",
                    "Implementar análisis de clientes perdidos",
                    "Establecer métricas de variación de ventas"
                ]
            },
            charts: {
                salesEvolution: tickets.slice(-7).map((ticket, index) => ({
                    period: new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000).toLocaleDateString('es-ES', { month: 'short' }),
                    sales: ticket.total || 0
                })),
                customerDistribution: {
                    champions: Math.floor(totalClientes * 0.05),
                    loyalCustomers: Math.floor(totalClientes * 0.15),
                    potentialLoyalists: Math.floor(totalClientes * 0.20),
                    newCustomers: Math.floor(totalClientes * 0.30),
                    promising: Math.floor(totalClientes * 0.15),
                    needAttention: Math.floor(totalClientes * 0.10),
                    aboutToSleep: Math.floor(totalClientes * 0.03),
                    atRisk: Math.floor(totalClientes * 0.02),
                    cannotLoseThem: 0,
                    hibernating: 0,
                    lost: 0
                },
                returnRate: 75.0, // Requiere análisis de backend
                satisfaction: {
                    npsScore: 0, // Requiere datos de encuestas
                    averageScore: 8.0, // Requiere datos de encuestas
                    totalResponses: 0 // Requiere datos de encuestas
                }
            },
            campaigns: {
                totalCampaigns: 0, // Requiere datos de campañas del backend
                activeCampaigns: 0, // Requiere datos de campañas del backend
                openRate: 0, // Requiere datos de campañas del backend
                clickRate: 0, // Requiere datos de campañas del backend
                redemptionRate: 0, // Requiere datos de campañas del backend
                estimatedROI: 0 // Requiere datos de campañas del backend
            },
            loyalty: {
                pointsAccumulated: puntosAcumulados,
                pointsRedeemed: puntosCanjeados,
                topLoyalCustomers: [], // Requiere análisis de backend
                popularRewards: marca.premios?.map(premio => ({
                    name: premio.nombre,
                    redemptions: Math.floor(Math.random() * 10) // Simulado
                })) || []
            },
            missingData: {
                historicalSales: true,
                customerAnalysis: true,
                satisfactionSurveys: true,
                campaignMetrics: true,
                detailedLoyalty: true
            }
        };

        console.log('✅ [RealData] Reporte generado con datos disponibles:', {
            reportId: baseData.reportId,
            marcaId: baseData.marcaId,
            marcaName: baseData.marcaName,
            period: baseData.period,
            ventasTotales: baseData.executiveSummary.totalSales,
            ticketPromedio: baseData.executiveSummary.averageTicket,
            totalClientes: totalClientes,
            puntosAcumulados: baseData.loyalty.pointsAccumulated,
            puntosCanjeados: baseData.loyalty.pointsRedeemed,
            datosFaltantes: baseData.missingData,
            timestamp: new Date().toISOString()
        });

        return baseData;
    };

    const handleMarcaChange = (selectedOption) => {
        console.log('🏢 [ReportingPage] Cambio de marca:', {
            marcaAnterior: selectedMarca?.nombre,
            marcaNueva: selectedOption?.nombre,
            marcaId: selectedOption?._id,
            timestamp: new Date().toISOString()
        });
        setSelectedMarca(selectedOption);
    };

    const handleReportTypeChange = (selectedOption) => {
        console.log('📊 [ReportingPage] Cambio de tipo de reporte:', {
            tipoAnterior: reportType,
            tipoNuevo: selectedOption.value,
            timestamp: new Date().toISOString()
        });
        setReportType(selectedOption.value);
    };

    const handlePeriodChange = (selectedOption) => {
        console.log('📅 [ReportingPage] Cambio de período:', {
            periodoAnterior: period,
            periodoNuevo: selectedOption.value,
            timestamp: new Date().toISOString()
        });
        setPeriod(selectedOption.value);
    };

    // Función para renderizar contenido específico por tipo de reporte
    const renderReportContent = (reportData, reportType) => {
        console.log('🎨 [RENDER CONTENT] Renderizando contenido del reporte:', {
            hasReportData: !!reportData,
            reportType,
            hasExecutiveSummary: !!reportData?.executiveSummary,
            hasCharts: !!reportData?.charts,
            hasRecommendations: !!(reportData?.executiveSummary?.topRecommendations || reportData?.recomendacionesAccionables || []).length
        });

        if (!reportData) {
            console.warn('⚠️ [RENDER CONTENT] No hay reportData, retornando null');
            return null;
        }

        const commonSections = (
            <>
                {/* Resumen Ejecutivo */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                        <FaChartLine className="mr-3 text-blue-500" />
                        Resumen Ejecutivo
                    </h3>

                    {/* Logs reducidos para debugging */}
                    {(() => {
                        const ventas = reportData.executiveSummary?.ventasActuales || reportData.executiveSummary?.totalSales || 0;
                        const clientes = reportData.executiveSummary?.nuevosClientes || reportData.executiveSummary?.newCustomers || 0;
                        const ticket = reportData.executiveSummary?.ticketMedioActual || reportData.executiveSummary?.averageTicket || 0;
                        console.log('📊 [REPORT] Resumen:', { ventas, clientes, ticket, periodo: period, hasRecommendations: !!(reportData.executiveSummary?.topRecommendations || reportData.recomendacionesAccionables)?.length });
                        return null;
                    })()}

                    {/* Alerta informativa si todos los valores son 0 */}
                    {(() => {
                        const ventas = reportData.executiveSummary?.ventasActuales || reportData.executiveSummary?.totalSales || 0;
                        const clientes = reportData.executiveSummary?.nuevosClientes || reportData.executiveSummary?.newCustomers || 0;
                        const ticket = reportData.executiveSummary?.ticketMedioActual || reportData.executiveSummary?.averageTicket || 0;

                        if (ventas === 0 && clientes === 0 && ticket === 0) {
                            return (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                    <div className="flex items-start">
                                        <FaInfoCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                                        <div>
                                            <h4 className="text-sm font-medium text-blue-800 mb-1">
                                                Información del Período
                                            </h4>
                                            <p className="text-sm text-blue-700">
                                                No se registraron ventas, nuevos clientes o transacciones en el período seleccionado ({periodOptions.find(p => p.value === period)?.label || period}).
                                                Esto puede deberse a que:
                                            </p>
                                            <ul className="text-xs text-blue-600 mt-2 space-y-1 list-disc list-inside">
                                                <li>El período seleccionado aún no tiene datos registrados</li>
                                                <li>La marca está comenzando y aún no tiene actividad</li>
                                                <li>Las transacciones se registraron en otro período</li>
                                            </ul>
                                            <p className="text-xs text-blue-600 mt-2">
                                                💡 <strong>Recomendación:</strong> Prueba con un período diferente o espera a que se registren más transacciones.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })()}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <MetricCard
                            title="Ventas Totales"
                            value={`€${((reportData.executiveSummary?.ventasActuales || reportData.executiveSummary?.totalSales || 0)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon={<FaMoneyBillWave className="text-blue-500" />}
                            trend={((reportData.executiveSummary?.variacionVentas !== undefined && reportData.executiveSummary?.variacionVentas !== 0) || (reportData.executiveSummary?.salesVariation !== undefined && reportData.executiveSummary?.salesVariation !== 0))
                                ? ((reportData.executiveSummary?.variacionVentas || reportData.executiveSummary?.salesVariation || 0) > 0 ? 'up' : 'down')
                                : null}
                            trendValue={reportData.executiveSummary?.variacionVentas !== undefined || reportData.executiveSummary?.salesVariation !== undefined
                                ? `${(reportData.executiveSummary?.variacionVentas || reportData.executiveSummary?.salesVariation || 0) >= 0 ? '+' : ''}${Math.abs(reportData.executiveSummary?.variacionVentas || reportData.executiveSummary?.salesVariation || 0).toFixed(1)}%`
                                : undefined}
                            tooltip="Total de ventas en el período"
                            color="blue"
                        />
                        <MetricCard
                            title="Ticket Promedio"
                            value={`€${(reportData.executiveSummary?.ticketMedioActual || reportData.executiveSummary?.averageTicket || 0).toFixed(2)}`}
                            icon={<FaChartLine className="text-green-500" />}
                            tooltip="Valor promedio por transacción"
                            color="green"
                        />
                        <MetricCard
                            title="Nuevos Clientes"
                            value={reportData.executiveSummary?.nuevosClientes || reportData.executiveSummary?.newCustomers || 0}
                            icon={<FaUsers className="text-purple-500" />}
                            tooltip="Clientes adquiridos en el período"
                            color="purple"
                        />
                        <MetricCard
                            title="Satisfacción"
                            value={`${(reportData.executiveSummary?.averageSatisfaction || 0).toFixed(1)}/5`}
                            icon={<FaEye className="text-orange-500" />}
                            tooltip={`Puntuación promedio de satisfacción. NPS: ${reportData.executiveSummary?.npsScore || 0}/100`}
                            color="orange"
                        />
                    </div>
                </div>

                {/* Recomendaciones de IA */}
                <AIRecommendations recommendations={
                    reportData.executiveSummary?.topRecommendations ||
                    reportData.recomendacionesAccionables ||
                    []
                } />

                {/* Gráficas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <EvolutionChart
                            data={reportData.charts?.salesEvolution || []}
                            title="Evolución de Ventas"
                            dataKey="totalSales"
                            color="#3b82f6"
                        />
                    </div>
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <CustomerDistributionChart data={reportData.charts?.customerDistribution || {}} />
                    </div>
                </div>
            </>
        );

        // Contenido específico por tipo de reporte
        switch (reportType) {
            case 'fidelizacion':
                return (
                    <>
                        {commonSections}
                        {/* Métricas de Fidelización */}
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                                <FaRetweet className="mr-3 text-purple-500" />
                                Programa de Fidelización
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <MetricCard
                                    title="Puntos Acumulados"
                                    value={(reportData.loyalty?.pointsAccumulated || 0).toLocaleString()}
                                    icon={<FaChartLine className="text-blue-500" />}
                                    tooltip="Total de puntos otorgados"
                                    color="blue"
                                />
                                <MetricCard
                                    title="Puntos Canjeados"
                                    value={(reportData.loyalty?.pointsRedeemed || 0).toLocaleString()}
                                    icon={<FaRetweet className="text-green-500" />}
                                    tooltip="Total de puntos redimidos"
                                    color="green"
                                />
                                <MetricCard
                                    title="Tasa de Redención"
                                    value={`${reportData.loyalty?.pointsAccumulated > 0 ? ((reportData.loyalty.pointsRedeemed / reportData.loyalty.pointsAccumulated) * 100).toFixed(1) : 0}%`}
                                    icon={<FaArrowUp className="text-purple-500" />}
                                    tooltip="Porcentaje de puntos canjeados"
                                    color="purple"
                                />
                                <MetricCard
                                    title="Tasa de Retorno"
                                    value={`${(reportData.charts?.returnRate || 0).toFixed(1)}%`}
                                    icon={<FaUsers className="text-orange-500" />}
                                    tooltip="Porcentaje de clientes que regresan"
                                    color="orange"
                                />
                            </div>
                        </div>
                    </>
                );

            case 'captacion':
                return (
                    <>
                        {commonSections}
                        <AcquisitionSources data={reportData.acquisition} />
                    </>
                );

            case 'ventas':
                return (
                    <>
                        {commonSections}
                        <ProductMetrics data={reportData.products} />
                    </>
                );

            case 'campanas':
                return (
                    <>
                        {commonSections}
                        {/* Métricas de Campañas */}
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                                <FaBullhorn className="mr-3 text-orange-500" />
                                Rendimiento de Campañas
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <MetricCard
                                    title="Campañas Activas"
                                    value={reportData.campaigns?.activeCampaigns || 0}
                                    icon={<FaStore className="text-purple-500" />}
                                    tooltip="Número de campañas activas"
                                    color="purple"
                                />
                                <MetricCard
                                    title="Tasa de Apertura"
                                    value={`${(reportData.campaigns?.openRate || 0).toFixed(1)}%`}
                                    icon={<FaEye className="text-blue-500" />}
                                    tooltip="Porcentaje de emails abiertos"
                                    color="blue"
                                />
                                <MetricCard
                                    title="Tasa de Clic"
                                    value={`${(reportData.campaigns?.clickRate || 0).toFixed(1)}%`}
                                    icon={<FaChartLine className="text-green-500" />}
                                    tooltip="Porcentaje de clics en campañas"
                                    color="green"
                                />
                                <MetricCard
                                    title="ROI Estimado"
                                    value={`${(reportData.campaigns?.estimatedROI || 0).toFixed(0)}%`}
                                    icon={<FaArrowUp className="text-orange-500" />}
                                    tooltip="Retorno de inversión estimado"
                                    color="orange"
                                />
                            </div>
                        </div>
                    </>
                );

            case 'reputacion':
                return (
                    <>
                        {commonSections}
                        <SentimentAnalysis data={reportData.sentiment} />
                    </>
                );

            case 'cohortes':
                return (
                    <>
                        {commonSections}
                        <CohortAnalysis data={reportData.cohorts} />
                    </>
                );

            case 'leads':
                return (
                    <>
                        {commonSections}
                        <LeadsFunnel data={reportData.leads} />
                    </>
                );

            default: // general
                return commonSections;
        }
    };

    // La generación asíncrona ahora se maneja en generateReport con polling en pollReportStatus

    // Función para descargar reporte generado en diferentes formatos
    const downloadGeneratedReport = async (reportId, format = 'pdf') => {
        if (!reportId) {
            Swal.fire('Error', 'No hay reporte disponible para descargar.', 'error');
            return;
        }

        try {
            setLoading(true);
            console.log(`📥 [DOWNLOAD] Descargando reporte ${reportId} en formato ${format}`);

            // Llamar al endpoint de descarga
            const response = await fetch(`/api/reporting/reports/${reportId}/download?format=${format}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
                }
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();

            const mimeTypes = {
                pdf: 'application/pdf',
                excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                html: 'text/html',
                csv: 'text/csv',
                json: 'application/json'
            };

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Obtener nombre del archivo desde headers o usar uno predeterminado
            const contentDisposition = response.headers.get('content-disposition');
            let filename = `reporte-${reportType}-${selectedMarca?.nombre || 'marca'}-${new Date().toISOString().split('T')[0]}.${format}`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            console.log('✅ [DOWNLOAD] Reporte descargado:', format);
            Swal.fire('Éxito', `Reporte descargado en formato ${format.toUpperCase()}.`, 'success');
        } catch (error) {
            console.error('❌ [DOWNLOAD] Error descargando reporte:', error);
            Swal.fire('Error', `No se pudo descargar el reporte en formato ${format}.`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Función para cargar un reporte desde la lista
    const handleLoadReport = async (report) => {
        try {
            setLoading(true);
            await loadReportData(report.reportId);
            setSelectedReport(report);
            setShowReportList(false);
            Swal.fire('Éxito', 'Reporte cargado exitosamente.', 'success');
        } catch (error) {
            console.error('❌ [REPORT] Error cargando reporte:', error);
            Swal.fire('Error', 'No se pudo cargar el reporte.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadReport = async () => {
        // Si hay un reportId del backend que empieza con "EJE-" u otros prefijos del servidor, usar descarga del servidor
        if (reportData?.reportId && (reportData.reportId.startsWith('EJE-') || reportData.reportId.startsWith('FID-') || reportData.reportId.startsWith('GEN-'))) {
            try {
                await downloadGeneratedReport(reportData.reportId, 'pdf');
                return;
            } catch (error) {
                console.warn('⚠️ [PDF] Error descargando del servidor, generando PDF cliente:', error);
                // Continuar con generación cliente
            }
        }

        // Si no hay reportId del servidor o falla, generar PDF cliente
        console.log('📄 [PDF] Iniciando descarga de PDF cliente:', {
            hasReportData: !!reportData,
            hasSelectedMarca: !!selectedMarca,
            marcaId: selectedMarca?._id,
            marcaNombre: selectedMarca?.nombre,
            timestamp: new Date().toISOString()
        });

        if (!reportData || !selectedMarca) {
            console.warn('⚠️ [PDF] No hay datos suficientes para generar PDF');
            Swal.fire('Error', 'No hay datos de reporte para descargar.', 'error');
            return;
        }

        try {
            setLoading(true);
            console.log('🔄 [PDF] Generando PDF...');

            // Obtener el tipo de reporte
            const selectedReportTypeObj = reportTypes.find(rt => rt.value === reportType);

            // Crear PDF
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Configuración de colores
            const primaryColor = '#3b82f6';
            const secondaryColor = '#6b7280';
            const accentColor = '#10b981';

            // Header con logo y título
            pdf.setFillColor(primaryColor);
            pdf.rect(0, 0, pageWidth, 30, 'F');

            // Logo PetitPrime (simulado con texto)
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.text('PetitPrime', 15, 20);

            // Título del reporte
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`${selectedReportTypeObj?.label || 'Reporte Ejecutivo'} - ${selectedMarca.nombre}`, pageWidth - 15, 20, { align: 'right' });

            // Fecha de generación
            pdf.setFontSize(10);
            pdf.text(`Generado: ${new Date(reportData.generatedAt).toLocaleDateString('es-ES')}`, pageWidth - 15, 25, { align: 'right' });

            // Resetear color
            pdf.setTextColor(0, 0, 0);

            // Resumen Ejecutivo
            let yPosition = 45;
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(primaryColor);
            pdf.text('Resumen Ejecutivo', 15, yPosition);

            yPosition += 10;
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(0, 0, 0);

            // Métricas principales en tabla
            const executiveSummary = reportData.executiveSummary || {};
            const ventas = executiveSummary.ventasActuales || executiveSummary.totalSales || 0;
            const ticket = executiveSummary.ticketMedioActual || executiveSummary.averageTicket || 0;
            const nuevosClientes = executiveSummary.nuevosClientes || executiveSummary.newCustomers || 0;
            const satisfaccion = executiveSummary.averageSatisfaction || 0;

            const metrics = [
                ['Métrica', 'Valor', 'Descripción'],
                ['Ventas Totales', `€${ventas.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Total de ventas en el período'],
                ['Ticket Promedio', `€${ticket.toFixed(2)}`, 'Valor promedio por transacción'],
                ['Nuevos Clientes', nuevosClientes.toString(), 'Clientes adquiridos en el período'],
                ['Satisfacción', `${satisfaccion.toFixed(1)}/10`, 'Puntuación promedio de satisfacción']
            ];

            // Dibujar tabla
            const tableTop = yPosition;
            const cellHeight = 8;
            const colWidths = [50, 40, 80];

            metrics.forEach((row, rowIndex) => {
                let xPosition = 15;
                row.forEach((cell, colIndex) => {
                    pdf.setFont('helvetica', rowIndex === 0 ? 'bold' : 'normal');
                    pdf.setFontSize(rowIndex === 0 ? 10 : 9);

                    if (rowIndex === 0) {
                        pdf.setFillColor(240, 240, 240);
                        pdf.rect(xPosition, tableTop + (rowIndex * cellHeight), colWidths[colIndex], cellHeight, 'F');
                    }

                    pdf.text(cell, xPosition + 2, tableTop + (rowIndex * cellHeight) + 5);
                    xPosition += colWidths[colIndex];
                });
            });

            yPosition = tableTop + (metrics.length * cellHeight) + 15;

            // Recomendaciones de IA
            if (reportData.executiveSummary?.topRecommendations && reportData.executiveSummary.topRecommendations.length > 0) {
                pdf.setFontSize(14);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(accentColor);
                pdf.text('Recomendaciones de IA', 15, yPosition);

                yPosition += 8;
                pdf.setFontSize(9);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(0, 0, 0);

                reportData.executiveSummary.topRecommendations.forEach((recommendation, index) => {
                    const text = `${index + 1}. ${recommendation}`;
                    const lines = pdf.splitTextToSize(text, pageWidth - 30);
                    pdf.text(lines, 15, yPosition);
                    yPosition += lines.length * 4 + 2;
                });

                yPosition += 10;
            }

            // Métricas de Campañas
            if (yPosition < pageHeight - 50) {
                const campaigns = reportData.campaigns || {};
                const campaignMetrics = campaigns.campaignMetrics || {};

                pdf.setFontSize(14);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(primaryColor);
                pdf.text('Rendimiento de Campañas', 15, yPosition);

                yPosition += 8;
                pdf.setFontSize(9);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(0, 0, 0);

                const campaignMetricsList = [
                    `Campañas Activas: ${campaignMetrics.activeCampaigns || campaigns.activeCampaigns || 0}`,
                    `Tasa de Apertura: ${(campaignMetrics.openRate || campaigns.openRate || 0).toFixed(1)}%`,
                    `Tasa de Clic: ${(campaignMetrics.clickRate || campaigns.clickRate || 0).toFixed(1)}%`,
                    `ROI Estimado: ${(campaignMetrics.estimatedROI || campaigns.estimatedROI || 0).toFixed(0)}%`
                ];

                campaignMetricsList.forEach(metric => {
                    pdf.text(metric, 15, yPosition);
                    yPosition += 5;
                });

                yPosition += 10;
            }

            // Métricas de Fidelización
            if (yPosition < pageHeight - 50) {
                const loyalty = reportData.loyalty || {};
                const loyaltyMetrics = loyalty.loyaltyMetrics || {};

                pdf.setFontSize(14);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(accentColor);
                pdf.text('Programa de Fidelización', 15, yPosition);

                yPosition += 8;
                pdf.setFontSize(9);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(0, 0, 0);

                const pointsAccumulated = loyaltyMetrics.pointsAccumulated || loyalty.pointsAccumulated || 0;
                const pointsRedeemed = loyaltyMetrics.pointsRedeemed || loyalty.pointsRedeemed || 0;
                const redemptionRate = loyaltyMetrics.redemptionRate || (pointsAccumulated > 0 ? (pointsRedeemed / pointsAccumulated * 100) : 0);
                const returnRate = reportData.charts?.returnRate || 0;

                const loyaltyMetricsList = [
                    `Puntos Acumulados: ${pointsAccumulated.toLocaleString('es-ES')}`,
                    `Puntos Canjeados: ${pointsRedeemed.toLocaleString('es-ES')}`,
                    `Tasa de Redención: ${redemptionRate.toFixed(1)}%`,
                    `Tasa de Retorno: ${returnRate.toFixed(1)}%`
                ];

                loyaltyMetricsList.forEach(metric => {
                    pdf.text(metric, 15, yPosition);
                    yPosition += 5;
                });
            }

            // Footer
            pdf.setFontSize(8);
            pdf.setTextColor(secondaryColor);
            pdf.text('Generado por PetitPrime - Plataforma de Fidelización', pageWidth / 2, pageHeight - 10, { align: 'center' });

            // Descargar PDF
            const fileName = `Reporte_${selectedMarca.nombre}_${new Date().toISOString().split('T')[0]}.pdf`;
            console.log('💾 [PDF] Descargando archivo:', fileName);
            pdf.save(fileName);

            console.log('✅ [PDF] PDF generado y descargado exitosamente:', {
                fileName,
                reportId: reportData.reportId,
                marcaId: selectedMarca._id,
                timestamp: new Date().toISOString()
            });

            Swal.fire({
                title: '¡PDF Generado!',
                text: 'El reporte se ha descargado exitosamente.',
                icon: 'success',
                confirmButtonText: 'Perfecto'
            });

        } catch (error) {
            console.error('❌ [PDF] Error generando PDF:', {
                error: error.message,
                stack: error.stack,
                reportId: reportData?.reportId,
                marcaId: selectedMarca?._id,
                timestamp: new Date().toISOString()
            });
            Swal.fire('Error', 'No se pudo generar el PDF. Por favor, inténtalo de nuevo.', 'error');
        } finally {
            setLoading(false);
            console.log('🏁 [PDF] Proceso de generación de PDF finalizado');
        }
    };

    const handleScheduleReport = () => {
        Swal.fire({
            title: 'Programar Reporte',
            text: 'Esta funcionalidad estará disponible próximamente.',
            icon: 'info',
            confirmButtonText: 'Entendido'
        });
    };

    if (!selectedMarca) {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="container mx-auto">
                    <div className="text-center py-12">
                        <FaSpinner className="animate-spin text-4xl text-blue-500 mx-auto mb-4" />
                        <p className="text-gray-600">Cargando marcas...</p>
                    </div>
                </div>
            </div>
        );
    }

    const marcaOptions = marcas.map((m) => ({
        value: m._id,
        label: m.nombre,
        ...m,
    }));

    const selectedReportType = reportTypes.find(rt => rt.value === reportType);

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="container mx-auto">
                {/* Header */}
                <header className="mb-12 text-center">
                    <h1 className="text-5xl font-bold text-gray-800 mb-4">
                        📊 Centro de Reportes
                    </h1>
                    <p className="text-xl text-gray-600 mb-8">
                        Genera reportes detallados y obtén insights accionables para tu negocio
                    </p>

                    {/* Controles */}
                    <div className="flex flex-wrap justify-center gap-4 mb-8">
                        <div className="w-64">
                            <Select
                                options={marcaOptions}
                                value={{ value: selectedMarca._id, label: selectedMarca.nombre, ...selectedMarca }}
                                onChange={handleMarcaChange}
                                placeholder="Selecciona una marca..."
                            />
                        </div>
                        <div className="w-48">
                            <Select
                                options={reportTypes}
                                value={selectedReportType}
                                onChange={handleReportTypeChange}
                                placeholder="Tipo de reporte..."
                            />
                        </div>
                        <div className="w-40">
                            <Select
                                options={periodOptions}
                                value={periodOptions.find(p => p.value === period)}
                                onChange={handlePeriodChange}
                                placeholder="Período..."
                            />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={generateReport}
                                disabled={loading || !selectedMarca}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center"
                            >
                                <FaChartLine className="mr-2" />
                                {loading ? 'Generando...' : 'Generar'}
                            </button>
                            <button
                                onClick={() => setShowReportList(!showReportList)}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                            >
                                <FaList className="mr-2" />
                                Reportes
                            </button>
                            {reportData && (
                                <>
                                    <button
                                        onClick={handleDownloadReport}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                                        title="Descargar PDF"
                                    >
                                        <FaDownload className="mr-2" />
                                        PDF
                                    </button>
                                </>
                            )}
                            <button
                                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
                            >
                                <FaCog className="mr-2" />
                                {showAdvancedOptions ? 'Ocultar' : 'Avanzadas'}
                            </button>
                        </div>
                    </div>
                </header>

                {/* Opciones Avanzadas */}
                {showAdvancedOptions && (
                    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">⚙️ Opciones Avanzadas</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Zona Horaria</label>
                                <select
                                    value={advancedParams.tz}
                                    onChange={(e) => setAdvancedParams({ ...advancedParams, tz: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="Europe/Madrid">Madrid (GMT+1)</option>
                                    <option value="America/Mexico_City">México (GMT-6)</option>
                                    <option value="America/New_York">Nueva York (GMT-5)</option>
                                    <option value="UTC">UTC (GMT+0)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Idioma</label>
                                <select
                                    value={advancedParams.locale}
                                    onChange={(e) => setAdvancedParams({ ...advancedParams, locale: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="es-ES">Español (España)</option>
                                    <option value="es-MX">Español (México)</option>
                                    <option value="en-US">English (US)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Moneda</label>
                                <select
                                    value={advancedParams.currency}
                                    onChange={(e) => setAdvancedParams({ ...advancedParams, currency: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="EUR">€ Euro</option>
                                    <option value="MXN">$ Peso Mexicano</option>
                                    <option value="USD">$ Dólar Americano</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Granularidad</label>
                                <select
                                    value={advancedParams.granularity}
                                    onChange={(e) => setAdvancedParams({ ...advancedParams, granularity: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="day">Diaria</option>
                                    <option value="week">Semanal</option>
                                    <option value="month">Mensual</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Formato</label>
                                <select
                                    value={advancedParams.format}
                                    onChange={(e) => setAdvancedParams({ ...advancedParams, format: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="json">JSON</option>
                                    <option value="html">HTML</option>
                                    <option value="pdf">PDF</option>
                                    <option value="csv">CSV</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Comparar con</label>
                                <select
                                    value={advancedParams.compare_to}
                                    onChange={(e) => setAdvancedParams({ ...advancedParams, compare_to: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="previous_period">Período Anterior</option>
                                    <option value="previous_year">Año Anterior</option>
                                    <option value="none">Sin Comparación</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}


                {/* Lista de Reportes Generados */}
                {showReportList && (
                    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-800 flex items-center">
                                <FaHistory className="mr-2" />
                                Reportes Generados
                            </h3>
                            <button
                                onClick={() => setShowReportList(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                        {generatedReports.length === 0 ? (
                            <p className="text-gray-600 text-center py-4">No hay reportes generados aún.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {generatedReports.map((report) => (
                                            <tr key={report.reportId} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm text-gray-900">{report.type}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900">{report.title || report.reportId.substring(0, 20)}...</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${report.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                        report.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                            'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        {report.status === 'completed' ? 'Completado' :
                                                            report.status === 'failed' ? 'Falló' : 'Generando'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {new Date(report.createdAt).toLocaleDateString('es-ES')}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleLoadReport(report)}
                                                            className="text-blue-600 hover:text-blue-800"
                                                            disabled={report.status !== 'completed'}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        {report.status === 'completed' && (
                                                            <>
                                                                <button
                                                                    onClick={() => downloadGeneratedReport(report.reportId, 'pdf')}
                                                                    className="text-red-600 hover:text-red-800"
                                                                    title="Descargar PDF"
                                                                >
                                                                    <FaDownload />
                                                                </button>
                                                                <button
                                                                    onClick={() => downloadGeneratedReport(report.reportId, 'excel')}
                                                                    className="text-green-600 hover:text-green-800"
                                                                    title="Descargar Excel"
                                                                >
                                                                    <FaDownload />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-12">
                        <FaSpinner className="animate-spin text-4xl text-blue-500 mx-auto mb-4" />
                        <p className="text-gray-600">Generando reporte...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                        <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-red-800 mb-2">Error al generar el reporte</h3>
                        <p className="text-red-600">{error}</p>
                        <button
                            onClick={generateReport}
                            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            Reintentar
                        </button>
                    </div>
                )}

                {/* Report Content */}
                {(() => {
                    console.log('🔍 [RENDER CHECK] Verificando renderizado del reporte:', {
                        hasReportData: !!reportData,
                        loading: loading,
                        shouldRender: !!(reportData && !loading),
                        reportId: reportData?.reportId,
                        hasExecutiveSummary: !!reportData?.executiveSummary
                    });
                    return null;
                })()}
                {reportData && !loading && (
                    <div ref={reportRef} className="space-y-8">
                        {/* Report Header */}
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-800">
                                        {selectedReportType?.label} - {selectedMarca.nombre}
                                    </h2>
                                    <p className="text-gray-600">
                                        Período: {periodOptions.find(p => p.value === period)?.label} |
                                        Generado: {new Date(reportData.generatedAt).toLocaleDateString('es-ES')}
                                    </p>
                                </div>
                                <div className="flex items-center text-green-600">
                                    <FaCheckCircle className="mr-2" />
                                    <span className="font-medium">Completado</span>
                                </div>
                            </div>
                        </div>

                        {/* Alerta de Datos Faltantes */}
                        {reportData.missingData && Object.values(reportData.missingData).some(Boolean) && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <FaExclamationTriangle className="text-yellow-600 mt-1 mr-3" />
                                    <div>
                                        <h3 className="text-sm font-medium text-yellow-800">
                                            Datos Limitados Disponibles
                                        </h3>
                                        <p className="text-sm text-yellow-700 mt-1">
                                            Este reporte se genera con los datos disponibles. Para obtener métricas completas,
                                            el backend necesita implementar:
                                        </p>
                                        <ul className="text-xs text-yellow-600 mt-2 space-y-1">
                                            {reportData.missingData.historicalSales && (
                                                <li>• Análisis de ventas históricas para variaciones</li>
                                            )}
                                            {reportData.missingData.customerAnalysis && (
                                                <li>• Análisis detallado de comportamiento de clientes</li>
                                            )}
                                            {reportData.missingData.satisfactionSurveys && (
                                                <li>• Sistema de encuestas de satisfacción (NPS)</li>
                                            )}
                                            {reportData.missingData.campaignMetrics && (
                                                <li>• Métricas de rendimiento de campañas</li>
                                            )}
                                            {reportData.missingData.detailedLoyalty && (
                                                <li>• Análisis detallado del programa de fidelización</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Información de Fuente de Datos */}
                        {reportData.dataSource && (
                            <div className={`border rounded-lg p-4 ${reportData.dataSource === 'statistics_endpoints'
                                ? 'bg-green-50 border-green-200'
                                : reportData.dataSource === 'backend_v2'
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-blue-50 border-blue-200'
                                }`}>
                                <div className="flex items-start">
                                    <FaCheckCircle className={`mt-1 mr-3 ${reportData.dataSource === 'statistics_endpoints'
                                        ? 'text-green-600'
                                        : reportData.dataSource === 'backend_v2'
                                            ? 'text-green-600'
                                            : 'text-blue-600'
                                        }`} />
                                    <div>
                                        <h3 className={`text-sm font-medium ${reportData.dataSource === 'statistics_endpoints'
                                            ? 'text-green-800'
                                            : reportData.dataSource === 'backend_v2'
                                                ? 'text-green-800'
                                                : 'text-blue-800'
                                            }`}>
                                            {reportData.dataSource === 'statistics_endpoints'
                                                ? 'Reporte generado con datos reales de estadísticas'
                                                : reportData.dataSource === 'backend_v2'
                                                    ? 'Datos del Backend v2.0 - API Completa'
                                                    : 'Datos Locales Generados'
                                            }
                                        </h3>
                                        <p className={`text-sm mt-1 ${reportData.dataSource === 'statistics_endpoints'
                                            ? 'text-green-700'
                                            : reportData.dataSource === 'backend_v2'
                                                ? 'text-green-700'
                                                : 'text-blue-700'
                                            }`}>
                                            {reportData.dataSource === 'statistics_endpoints'
                                                ? 'Este reporte usa los mismos endpoints y cálculos que las páginas de estadísticas, garantizando datos precisos y actualizados.'
                                                : reportData.dataSource === 'backend_v2'
                                                    ? 'Reporte generado con la nueva API v2.0 del backend. Incluye análisis RFM completo, métricas avanzadas y recomendaciones de IA.'
                                                    : 'Reporte generado con datos locales disponibles. Algunas métricas pueden estar limitadas.'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Contenido específico del reporte */}
                        {renderReportContent(reportData, reportType)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportingPage;
